import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';

import Joyride, { CallBackProps, Step, STATUS } from 'react-joyride';

import { TOUR_CONFIGS, TourId, TOUR_TRANSITION_DELAY_MS } from './config';
import { TourTooltip } from './TourTooltip';
import { TourDisplayBehavior } from './tourTracker';
import { useTourTracking } from './useTourTracking';
import { useFeatureFlag } from '../context/FeatureFlagContext';
import './tour.styles.scss';

type TourContextValue = {
  currentTourId: TourId | null;
  run: boolean;
  steps: Step[];
  startTour: (tourId: TourId, overrides?: { steps?: Step[] }) => void;
  stopTour: () => void;
  dismissTourPermanently: (tourId: string) => Promise<void>;
  tourDataLoading: boolean;
};

const TourContext = createContext<TourContextValue | undefined>(undefined);

type TourProviderProps = {
  children: ReactNode;
};

export const TourProvider = ({ children }: TourProviderProps) => {
  const [currentTourId, setCurrentTourId] = useState<TourId | null>(null);
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [tourQueue, setTourQueue] = useState<
    Array<{ tourId: TourId; overrides?: { steps?: Step[] } }>
  >([]);
  const hasRecordedDisplayRef = useRef<string | null>(null);
  const timerRefs = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const {
    recordTourDisplay,
    shouldDisplayTour: checkShouldDisplayTour,
    dismissTourPermanently,
    loading: tourDataLoading,
  } = useTourTracking();

  const isToursEnabled = useFeatureFlag('tours');

  const processNextTour = useCallback(
    (tourId: TourId, overrides?: { steps?: Step[] }) => {
      const config = TOUR_CONFIGS[tourId];
      if (!config) {
        console.warn(`[Tour] Configuration not found for: ${tourId}`);
        return false;
      }

      const displayBehavior: TourDisplayBehavior = config.displayBehavior ?? 'always';

      const shouldDisplay = checkShouldDisplayTour(tourId, displayBehavior);
      if (!shouldDisplay) {
        return false;
      }

      const baseSteps = config.steps;
      const mergedSteps = overrides?.steps ?? baseSteps;

      if (!mergedSteps.length) {
        console.warn(`[Tour] No steps defined for tour "${tourId}"`);
        return false;
      }

      setCurrentTourId(tourId);
      setSteps(mergedSteps);
      setRun(true);
      hasRecordedDisplayRef.current = null;
      return true;
    },
    [checkShouldDisplayTour],
  );

  const startTour = useCallback(
    (tourId: TourId, overrides?: { steps?: Step[] }) => {
      if (!isToursEnabled) {
        return;
      }

      if (tourDataLoading) {
        return;
      }

      if (run) {
        setTourQueue((prev) => [...prev, { tourId, overrides }]);
        return;
      }

      const started = processNextTour(tourId, overrides);
      if (!started) {
        return;
      }
    },
    [isToursEnabled, tourDataLoading, run, processNextTour],
  );

  const stopTour = useCallback(() => {
    // Clear any pending timers to prevent state updates after unmount
    timerRefs.current.forEach((timer) => clearTimeout(timer));
    timerRefs.current = [];

    setRun(false);
    setCurrentTourId(null);
    setSteps([]);
    hasRecordedDisplayRef.current = null;

    setTourQueue((prev) => {
      if (prev.length === 0) {
        return prev;
      }

      const processNextInQueue = () => {
        setTourQueue((currentQueue) => {
          if (currentQueue.length === 0) {
            return currentQueue;
          }

          const nextTour = currentQueue[0];
          const started = processNextTour(nextTour.tourId, nextTour.overrides);

          if (!started) {
            const remaining = currentQueue.slice(1);
            if (remaining.length > 0) {
              const timer = setTimeout(processNextInQueue, TOUR_TRANSITION_DELAY_MS);
              timerRefs.current.push(timer);
            }
            return remaining;
          }

          return currentQueue.slice(1);
        });
      };

      const timer = setTimeout(processNextInQueue, TOUR_TRANSITION_DELAY_MS);
      timerRefs.current.push(timer);
      return prev;
    });
  }, [processNextTour]);

  const handleCallback = useCallback(
    (data: CallBackProps) => {
      const { status } = data;

      if (
        status === STATUS.RUNNING &&
        currentTourId &&
        hasRecordedDisplayRef.current !== currentTourId
      ) {
        hasRecordedDisplayRef.current = currentTourId;
        recordTourDisplay(currentTourId).catch((error) => {
          console.warn(`[Tour] Failed to record display for ${currentTourId}:`, error);
        });
      }

      if (status === STATUS.SKIPPED) {
        stopTour();
        return;
      }

      if (status === STATUS.FINISHED) {
        stopTour();
      }
    },
    [stopTour, currentTourId, recordTourDisplay],
  );

  // Cleanup timers on unmount to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      timerRefs.current.forEach((timer) => clearTimeout(timer));
      timerRefs.current = [];
    };
  }, []);

  const value = useMemo(
    () => ({
      currentTourId,
      run,
      steps,
      startTour,
      stopTour,
      dismissTourPermanently,
      tourDataLoading,
    }),
    [currentTourId, run, steps, startTour, stopTour, dismissTourPermanently, tourDataLoading],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      <Joyride
        steps={steps}
        run={run}
        continuous={steps.length > 1}
        showSkipButton={false}
        scrollToFirstStep
        disableOverlayClose={false}
        callback={handleCallback}
        tooltipComponent={TourTooltip}
        styles={{
          options: {
            zIndex: 9999,
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
      />
    </TourContext.Provider>
  );
};

export const useTourContext = (): TourContextValue => {
  const ctx = useContext(TourContext);

  if (!ctx) {
    throw new Error('useTourContext must be used within a TourProvider');
  }

  return ctx;
};
